import {useRef} from 'react';
import type {RefObject} from 'react';

/**
 * Holds the value from the latest render in a ref, for reading *later* — after an `await`, from an
 * effect, or from a handler built once with `useCallback(fn, [])`.
 *
 * Assigning `ref.current` in the render body is the one render-phase ref write this codebase
 * sanctions: the ref is write-only during render, and the write is idempotent.
 *
 * Contract:
 *
 * - **Never read `.current` during render.** Render must read the value itself; reading through the
 *   ref decouples the output from the render that produced it.
 * - **Never use it as a dependency.** The ref's identity never changes, so a hook that depends on
 *   it never re-runs.
 * - **Not a substitute for `useEffectEvent`.** Use an effect event when the latest-value read
 *   belongs to an effect this component owns; use this when the reader is a callback handed down to
 *   children, which an effect event must never be.
 *
 * It exists so that post-await reads and referentially stable `useCallback(fn, [])` handlers see
 * live values — the `this.props` / `this.state` semantics a class component got for free.
 */
export function useLatestRef<T>(value: T): RefObject<T> {
  const ref = useRef(value);
  // oxlint-disable-next-line react/refs -- the render-synced write is the hook's contract; nothing reads the ref during render
  ref.current = value;

  return ref;
}
